// @flow

/**
 * Markdown to pdfmake Conversion Utilities
 * Converts markdown text to pdfmake document definition
 */

type PdfContent = Object | string | Array<Object | string>;

/**
 * Parse inline formatting and return pdfmake text array
 */
const parseInlineFormatting = (text: string): Array<Object | string> => {
  const result: Array<Object | string> = [];
  
  // Regex to match **bold**, *italic*, `code`
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
  
  let lastIndex = 0;
  let match;
  
  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }
    
    // Add formatted text
    if (match[2]) {
      // Bold **text**
      result.push({ text: match[2], bold: true });
    } else if (match[3]) {
      // Italic *text*
      result.push({ text: match[3], italics: true });
    } else if (match[4]) {
      // Code `text`
      result.push({ 
        text: match[4], 
        background: '#E8E8E8',
        font: 'Courier',
        fontSize: 10,
      });
    }
    
    lastIndex = regex.lastIndex;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }
  
  // If no formatting found, return the whole text
  if (result.length === 0) {
    return [text];
  }
  
  return result;
};

/**
 * Detect heading level from markdown line
 */
const parseHeading = (line: string): { level: number, text: string } | null => {
  const match = line.match(/^(#{1,6})\s+(.+)$/);
  if (match) {
    return { level: match[1].length, text: match[2] };
  }
  return null;
};

/**
 * Parse markdown table into pdfmake table structure
 */
const parseTable = (lines: string[], startIndex: number): { table: Object, endIndex: number } | null => {
  const rows: string[][] = [];
  let i = startIndex;
  
  while (i < lines.length) {
    const line = lines[i].trim();
    
    if (!line.includes('|')) {
      break;
    }
    
    // Skip delimiter row
    if (/^\|?\s*[-:]+\s*\|/.test(line)) {
      i++;
      continue;
    }
    
    const cells = line
      .split('|')
      .map(cell => cell.trim())
      .filter((cell, idx, arr) => !(idx === 0 && cell === '') && !(idx === arr.length - 1 && cell === ''));
    
    if (cells.length > 0) {
      rows.push(cells);
    }
    
    i++;
  }
  
  if (rows.length === 0) {
    return null;
  }
  
  // Convert to pdfmake table format
  const body = rows.map((row, rowIndex) => 
    row.map(cell => ({
      text: parseInlineFormatting(cell),
      fillColor: rowIndex === 0 ? '#E8E8E8' : null,
      bold: rowIndex === 0,
    }))
  );
  
  return {
    table: {
      table: {
        headerRows: 1,
        widths: Array(rows[0]?.length || 1).fill('*'),
        body,
      },
      layout: 'lightHorizontalLines',
      margin: [0, 8, 0, 12],
    },
    endIndex: i,
  };
};

/**
 * Convert markdown string to pdfmake document definition
 * @param {string} markdown - Markdown text (may contain {{CHART_IMAGE:id}} tokens)
 * @param {Object} chartImages - Optional map of displayId → PNG data URL
 */
export const markdownToPdfmake = (markdown: string, chartImages?: { [string]: string }): Object => {
  const lines = markdown.split('\n');
  const content: PdfContent[] = [];
  
  let i = 0;
  let inCodeBlock = false;
  let codeBlockLines: string[] = [];
  let previousLineWasBlank = false;

  const headingSizes = {
    1: 24,
    2: 20,
    3: 16,
    4: 14,
    5: 12,
    6: 11,
  };
  
  while (i < lines.length) {
    const line = lines[i];
    
    // Handle code blocks
    if (line.trim().startsWith('```')) {
      if (inCodeBlock) {
        // End code block
        content.push({
          text: codeBlockLines.join('\n'),
          font: 'Courier',
          fontSize: 9,
          background: '#F5F5F5',
          margin: [0, 5, 0, 10],
          preserveLeadingSpaces: true,
        });
        codeBlockLines = [];
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      i++;
      continue;
    }
    
    if (inCodeBlock) {
      codeBlockLines.push(line);
      i++;
      continue;
    }
    
    // Handle empty lines — set flag instead of pushing a visible space
    if (line.trim() === '') {
      previousLineWasBlank = true;
      i++;
      continue;
    }

    // Helper: apply extra top margin after a blank line
    const applyBlankLineSpacing = (element: Object) => {
      if (previousLineWasBlank) {
        const m = element.margin || [0, 0, 0, 0];
        element.margin = [m[0], m[1] + 8, m[2], m[3]];
        previousLineWasBlank = false;
      }
    };

    // Handle headings
    const heading = parseHeading(line);
    if (heading) {
      const el = {
        text: parseInlineFormatting(heading.text),
        fontSize: headingSizes[heading.level] || 14,
        bold: true,
        margin: [0, 12, 0, 6],
      };
      applyBlankLineSpacing(el);
      content.push(el);
      i++;
      continue;
    }

    // Handle tables
    if (line.includes('|')) {
      const tableResult = parseTable(lines, i);
      if (tableResult) {
        tableResult.table.margin = tableResult.table.margin || [0, 8, 0, 12];
        applyBlankLineSpacing(tableResult.table);
        content.push(tableResult.table);
        i = tableResult.endIndex;
        previousLineWasBlank = false;
        continue;
      }
    }

    // Handle unordered lists — group consecutive items at the same indent level
    const ulMatch = line.match(/^(\s*)[-*+]\s+(.+)$/);
    if (ulMatch) {
      const indent = ulMatch[1].length;
      const items = [];
      while (i < lines.length) {
        const m = lines[i].match(/^(\s*)[-*+]\s+(.+)$/);
        if (!m || m[1].length !== indent) break;
        items.push({ text: parseInlineFormatting(m[2]) });
        i++;
      }
      const el = { ul: items, margin: [indent * 10, 2, 0, 3] };
      applyBlankLineSpacing(el);
      content.push(el);
      continue;
    }

    // Handle ordered lists — group consecutive items at the same indent level
    const olMatch = line.match(/^(\s*)\d+\.\s+(.+)$/);
    if (olMatch) {
      const indent = olMatch[1].length;
      const items = [];
      while (i < lines.length) {
        const m = lines[i].match(/^(\s*)\d+\.\s+(.+)$/);
        if (!m || m[1].length !== indent) break;
        items.push({ text: parseInlineFormatting(m[2]) });
        i++;
      }
      const el = { ol: items, margin: [indent * 10, 2, 0, 3] };
      applyBlankLineSpacing(el);
      content.push(el);
      continue;
    }

    // Handle blockquotes
    const bqMatch = line.match(/^>\s*(.*)$/);
    if (bqMatch) {
      const el = {
        text: parseInlineFormatting(bqMatch[1]),
        margin: [20, 5, 0, 6],
        italics: true,
        color: '#555555',
      };
      applyBlankLineSpacing(el);
      content.push(el);
      i++;
      continue;
    }

    // Handle chart image tokens: {{CHART_IMAGE:displayId}}
    const chartMatch = line.trim().match(/^\{\{CHART_IMAGE:(.+?)\}\}$/);
    if (chartMatch && chartImages && chartImages[chartMatch[1]]) {
      const el = {
        image: chartImages[chartMatch[1]],
        width: 500,
        alignment: 'center',
        margin: [0, 8, 0, 12],
      };
      applyBlankLineSpacing(el);
      content.push(el);
      i++;
      continue;
    }

    // Default: regular paragraph
    const el = {
      text: parseInlineFormatting(line),
      margin: [0, 0, 0, 5],
    };
    applyBlankLineSpacing(el);
    content.push(el);
    i++;
  }
  
  return {
    content,
    defaultStyle: {
      font: 'Helvetica',
      fontSize: 11,
      lineHeight: 1.3,
    },
    styles: {
      header: {
        fontSize: 18,
        bold: true,
        margin: [0, 0, 0, 10],
      },
    },
  };
};
